import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import {
  documentService,
  researchProjectService,
  buildFileUrl,
} from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import ImageViewer from "../../components/ImageViewer";

const DocumentManagement = () => {
  const { isEstudiante } = useAuth();
  const canCreate = isEstudiante(); // Solo collaborators pueden crear documentos
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [registeringDocumentId, setRegisteringDocumentId] = useState(null);

  // State para ImageViewer
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState("");
  const [selectedImageTitle, setSelectedImageTitle] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    projectId: "",
  });

  useEffect(() => {
    loadDocuments();
    loadProjects();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await documentService.getAll();
      setDocuments(response.data);
    } catch (err) {
      setError("Error al cargar documentos");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await researchProjectService.getAll();
      // Si es collaborator, solo mostrar proyectos activos donde puede subir documentos
      if (isEstudiante()) {
        // Filtrar proyectos activos (status = "active")
        const activeProjects = response.data.filter(
          (project) => project.status === "active"
        );
        setProjects(activeProjects);
      } else {
        setProjects(response.data);
      }
    } catch (err) {
      console.error("Error al cargar proyectos:", err);
    }
  };

  const handleOpenDialog = (document = null) => {
    if (document) {
      setEditingDocument(document);
      setFormData({
        title: document.title,
        description: document.description || "",
        projectId: document.projectId || "",
      });
    } else {
      setEditingDocument(null);
      setFormData({
        title: "",
        description: "",
        projectId: "",
      });
    }
    setSelectedFile(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDocument(null);
    setSelectedFile(null);
    setError("");
    setSuccess("");
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingDocument && !selectedFile) {
      setError("Por favor selecciona un archivo");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("description", formData.description || "");

      if (formData.projectId) {
        formDataToSend.append("projectId", formData.projectId);
      }

      if (selectedFile) {
        formDataToSend.append("file", selectedFile);
      }

      if (editingDocument) {
        await documentService.update(editingDocument.id, formDataToSend);
        setSuccess("Documento actualizado exitosamente");
      } else {
        await documentService.create(formDataToSend);
        setSuccess("Documento creado exitosamente");
      }

      setTimeout(() => {
        handleCloseDialog();
        loadDocuments();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar documento");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este documento?")) return;

    try {
      await documentService.delete(id);
      setSuccess("Documento eliminado exitosamente");
      loadDocuments();
    } catch (err) {
      setError("Error al eliminar documento");
    }
  };

  const handleRegisterBlockchain = async (id) => {
    if (
      !window.confirm(
        "¿Deseas registrar este documento en la blockchain? El archivo se subirá a IPFS."
      )
    ) {
      return;
    }

    try {
      setRegisteringDocumentId(id);
      setError("");
      await documentService.registerBlockchain(id);
      setSuccess(
        "Documento registrado en blockchain exitosamente. El archivo se ha subido a IPFS."
      );
      loadDocuments();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Error al registrar documento en blockchain"
      );
    } finally {
      setRegisteringDocumentId(null);
    }
  };

  const handleShowInfo = (document) => {
    setSelectedDocument(document);
    setOpenInfoDialog(true);
  };

  const handleDownload = async (id) => {
    try {
      const response = await documentService.download(id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `document-${id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Error al descargar documento");
    }
  };

  const getStatusColor = (document) => {
    if (document.blockchainTxHash) {
      return document.status === "revoked" ? "error" : "success";
    }
    return "warning";
  };

  const getStatusText = (document) => {
    if (document.blockchainTxHash) {
      return document.status === "revoked" ? "REVOCADO" : "VERIFICADO";
    }
    return "PENDIENTE";
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h4" component="h1">
              Gestión de Investigaciones
            </Typography>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Nuevo Documento
              </Button>
            )}
          </Box>
        </Paper>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Documents Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Proyecto</TableCell>
                <TableCell>Almacenamiento</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((document) => (
                <TableRow key={document.id}>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {document.title}
                    </Typography>
                    {document.description && (
                      <Typography variant="body2" color="textSecondary">
                        {document.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {document.ResearchProject ? (
                      <Chip
                        label={document.ResearchProject.title}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Sin proyecto
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={document.storageType?.toUpperCase() || "LOCAL"}
                      size="small"
                      color={
                        document.storageType === "ipfs" ? "success" : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(document)}
                      color={getStatusColor(document)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(document.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleShowInfo(document)}
                      title="Ver información"
                      sx={{ color: "#1976d2" }}
                    >
                      <ViewIcon />
                    </IconButton>

                    {canCreate && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(document)}
                        title="Editar"
                        sx={{ color: "#2e7d32" }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}

                    {canCreate && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(document.id)}
                        title="Eliminar"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {documents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No hay documentos registrados
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingDocument ? "Editar Documento" : "Nuevo Documento"}
          </DialogTitle>
          <DialogContent>
            {/* Alerts dentro del modal */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            {submitting && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} />
                  {editingDocument
                    ? "Actualizando documento..."
                    : "Creando documento..."}
                </Box>
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                name="title"
                label="Título"
                value={formData.title}
                onChange={handleInputChange}
                required
              />

              <TextField
                fullWidth
                margin="normal"
                name="description"
                label="Descripción"
                multiline
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Proyecto (Opcional)</InputLabel>
                <Select
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleInputChange}
                  label="Proyecto (Opcional)"
                >
                  <MenuItem value="">Sin proyecto</MenuItem>
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.title}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      No hay proyectos disponibles
                    </MenuItem>
                  )}
                </Select>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mt: 1 }}
                >
                  Puedes asociar el documento a un proyecto o dejarlo sin
                  proyecto
                </Typography>
              </FormControl>

              <Box sx={{ mt: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  fullWidth
                >
                  {selectedFile ? selectedFile.name : "Seleccionar Archivo"}
                  <input
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                  />
                </Button>
                {!editingDocument && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    Archivo requerido para nuevos documentos
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              {submitting
                ? editingDocument
                  ? "Actualizando..."
                  : "Creando..."
                : editingDocument
                ? "Actualizar"
                : "Crear"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Info Dialog */}
        <Dialog
          open={openInfoDialog}
          onClose={() => setOpenInfoDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Información del Documento</DialogTitle>
          <DialogContent>
            {selectedDocument && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedDocument.title}
                </Typography>

                {selectedDocument.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Descripción:</Typography>
                    <Typography variant="body2" paragraph>
                      {selectedDocument.description}
                    </Typography>
                  </Box>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Tipo:</Typography>
                    <Chip
                      label={selectedDocument.documentType}
                      size="small"
                      color="primary"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Almacenamiento:</Typography>
                    <Chip
                      label={selectedDocument.storageType}
                      size="small"
                      color={
                        selectedDocument.storageType === "ipfs"
                          ? "success"
                          : "default"
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Estado:</Typography>
                    <Chip
                      label={getStatusText(selectedDocument)}
                      color={getStatusColor(selectedDocument)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">
                      Fecha de creación:
                    </Typography>
                    <Typography variant="body2">
                      {new Date(selectedDocument.createdAt).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>

                {selectedDocument.fileHash && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">
                      Hash del Archivo:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        wordBreak: "break-all",
                        backgroundColor: "#f5f5f5",
                        padding: 1,
                        borderRadius: 1,
                      }}
                    >
                      {selectedDocument.fileHash}
                    </Typography>
                  </Box>
                )}

                {selectedDocument.blockchainTxHash && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">
                      Transaction Hash:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        wordBreak: "break-all",
                        backgroundColor: "#e8f5e8",
                        padding: 1,
                        borderRadius: 1,
                      }}
                    >
                      {selectedDocument.blockchainTxHash}
                    </Typography>
                  </Box>
                )}

                {selectedDocument.storagePath && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Archivo:</Typography>
                    {selectedDocument.storageType === "local" ? (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.8rem",
                            backgroundColor: "#f0f0f0",
                            padding: 1,
                            borderRadius: 1,
                            mb: 1,
                          }}
                        >
                          {selectedDocument.storagePath}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const fileUrl = buildFileUrl(
                                selectedDocument.storagePath,
                                "documents"
                              );
                              setSelectedImageSrc(fileUrl);
                              setSelectedImageTitle(selectedDocument.title);
                              setImageViewerOpen(true);
                            }}
                            startIcon={<ViewIcon />}
                          >
                            Ver Archivo
                          </Button>
                        </Box>
                      </Box>
                    ) : selectedDocument.storageType === "ipfs" ? (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.8rem",
                            backgroundColor: "#e8f5e8",
                            padding: 1,
                            borderRadius: 1,
                            mb: 1,
                          }}
                        >
                          IPFS Hash: {selectedDocument.storagePath}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const ipfsUrl = `https://gateway.pinata.cloud/ipfs/${selectedDocument.storagePath}`;
                              setSelectedImageSrc(ipfsUrl);
                              setSelectedImageTitle(selectedDocument.title);
                              setImageViewerOpen(true);
                            }}
                            startIcon={<ViewIcon />}
                          >
                            Ver Archivo
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            href={`https://gateway.pinata.cloud/ipfs/${selectedDocument.storagePath}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            color="info"
                          >
                            Ver en IPFS
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.8rem",
                          backgroundColor: "#fff3cd",
                          padding: 1,
                          borderRadius: 1,
                        }}
                      >
                        {selectedDocument.storagePath}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => handleDownload(selectedDocument?.id)}
              startIcon={<DownloadIcon />}
            >
              Descargar
            </Button>
            <Button onClick={() => setOpenInfoDialog(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        {/* Image Viewer */}
        <ImageViewer
          open={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          imageSrc={selectedImageSrc}
          imageTitle={selectedImageTitle}
        />
      </Box>
    </Container>
  );
};

export default DocumentManagement;
